<template>
  <V1ScreenScaffold
    :title="t('InstallmentsList-Title')"
    :next-disabled="!canContinue"
    data-testid="sp-installments"
    @next="handleContinue"
  >
    <template v-if="payBeforeDate" #subtitle>
      <p class="sp-pay-before" data-testid="sp-pay-before">
        <span v-if="payBeforeDate.isOverdue">
          {{ payBeforeDatePrefix }}
        </span>
        <span v-else>
          {{ t("pay-before") }}
        </span>
        {{ payBeforeDate.datetime }}
      </p>
    </template>

    <RadioGroup
      :model-value="selectedValue"
      :options="options"
      :aria-label="t('InstallmentsList-Title')"
      data-testid="sp-options"
      @update:model-value="selectedValue = String($event)"
    />

    <CustomerioBanner />
  </V1ScreenScaffold>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, ref } from "vue";
import { RadioGroup, RadioGroupOption } from "@payjoy/design-system";
import { ISuperPacmanInstallment, ISuperPacmanInstallmentItem, ISuperPacmanDaysToAmountItem } from "@/types/ISuperPacman";
import V1ScreenScaffold from "@/components/v1/V1ScreenScaffold.vue";
import CustomerioBanner from "@/components/CustomerioBanner.vue";
import { useI18n } from "vue-i18n";
import { formatPayBeforeDateTime } from "@/shared-helpers/date-time.helper";
import { useStore } from "vuex";
import { getFallbackLocale } from "@/i18n";
import { otherAmountSubtitleKey } from "@/shared-helpers/otherAmountOptions.helper";
import { IInstallmentDetail } from "@/types/IInstallment";
import { IPayOptionsState } from "@/types/IPayOptionsState";
import { Countries } from "@/types/Countries";

const OTHER_AMOUNT_VALUE = "other-amount";

export default defineComponent({
  name: "SuperPacmanInstallmentList",
  components: {
    V1ScreenScaffold,
    RadioGroup,
    CustomerioBanner,
  },
  props: {
    installmentDetails: {
      type: Object as PropType<ISuperPacmanInstallment>,
      required: true,
    },
  },
  emits: ["select-installment-item", "other-amount"],
  setup(props, { emit }) {
    const { t, n, locale } = useI18n();
    const store = useStore<{ payOptions: IPayOptionsState }>();

    const installment = computed(() => props.installmentDetails);
    const financeProduct = computed(() => installment.value.financeProduct);
    const isMexico = computed(() => financeProduct.value.country === Countries.MEXICO);
    const selectedValue = ref<string | null>(null);

    const computeAmountDisplay = (amount: string): string => {
      const currencyLocale = getFallbackLocale(store.state.payOptions.deviceInfo.country ?? locale.value);
      const rounded = isMexico.value ? Math.round(Number(amount)) : Number(amount);
      return n(rounded, "currency", currencyLocale).replace(/\u00A0/g, "");
    };

    const firstRow = computed(() => installment.value.daysToAmount[0] ?? null);
    const isBrazilSubscription = computed(
      () =>
        financeProduct.value.country === Countries.BRAZIL &&
        financeProduct.value.subProductVersion === "Subscription",
    );
    const twoWeekRow = computed(() =>
      isBrazilSubscription.value ? installment.value.daysToAmount.find((row) => row.daysPaid === 14) ?? null : null,
    );

    const amountOptionsByTerm = computed<Record<string, ISuperPacmanDaysToAmountItem>>(() => {
      const map: Record<string, ISuperPacmanDaysToAmountItem> = {};
      if (firstRow.value) {
        map.week = firstRow.value;
      }
      if (twoWeekRow.value) {
        map.week2 = twoWeekRow.value;
      }
      return map;
    });

    const options = computed<RadioGroupOption[]>(() => {
      const built: RadioGroupOption[] = Object.entries(amountOptionsByTerm.value).map(([term, row]) => ({
        value: term,
        title: computeAmountDisplay(row.amount),
        subtitle: t(term),
      }));
      built.push({
        value: OTHER_AMOUNT_VALUE,
        title: t("superpacman-other-amount"),
        subtitle: t(otherAmountSubtitleKey(installment.value)),
      });
      return built;
    });

    const canContinue = computed<boolean>(() => selectedValue.value !== null);

    const payBeforeDate = computed(() => {
      const timestamp = Number(installment.value.validThrough?.currentValidThrough);
      if (!timestamp) return null;
      const isOverdue = Date.now() > timestamp * 1000;
      const datetime = formatPayBeforeDateTime(timestamp);
      return { datetime, isOverdue };
    });

    const payBeforeDatePrefix = computed(() => {
      let art = "";
      if (locale.value === "pt-BR") {
        art = "na";
        if (["sáb", "dom"].some((p) => payBeforeDate.value?.datetime.startsWith(p))) {
          art = "no";
        }
      }
      return t("overdue-on", { art });
    });

    const selectAmount = (term: string): void => {
      const row = amountOptionsByTerm.value[term];
      if (!row) return;
      const amount = Number(row.amount);
      const installmentDetail: IInstallmentDetail = {
        amount,
        term,
        isOverPayment: false,
        days: 0,
      };
      const acceptanceToken = store.state.payOptions.selectedInstallment?.acceptanceToken;
      store.dispatch("payOptions/setSelectedInstallment", installmentDetail);
      store.dispatch("payOptions/setWompiAcceptanceToken", acceptanceToken);
      store.dispatch("payOptions/setSuperPacmanAmount", amount);
      store.dispatch("payOptions/setIsOtherAmount", false);
      const item: ISuperPacmanInstallmentItem = {
        originalLabel: term,
        label: t(term),
        amount: row.amount,
        currency: financeProduct.value.currency,
      };
      emit("select-installment-item", item);
    };

    const handleContinue = (): void => {
      if (selectedValue.value === null) return;
      if (selectedValue.value === OTHER_AMOUNT_VALUE) {
        emit("other-amount");
        return;
      }
      selectAmount(selectedValue.value);
    };

    return {
      t,
      selectedValue,
      options,
      canContinue,
      payBeforeDate,
      payBeforeDatePrefix,
      handleContinue,
    };
  },
});
</script>

<style scoped>
.sp-pay-before {
  margin: 0;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: var(--pj-color-brand-gray-850, #222926);
}
</style>
